import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import RoundButton from 'shared/buttons/RoundButton';
import MobileRemove from 'shared/layout/MobileRemove';
import Separator from 'shared/layout/Separator';
import Spacer from 'shared/layout/Spacer';
import styled from 'styled-components';

import useQueryParams from '@hooks/use-query-params';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useFormLoad from 'hooks/useFormLoad';
import usePreviewCheck from 'hooks/usePreviewCheck';
import JobApplication from 'models/JobApplication';
import { useStore } from 'models/stores';

import AcceptTerms from './AcceptTerms';
import BackButton from './BackButton';
import FileField from './FileField';
import InputField from './InputField';
import JobHeader from './JobHeader';
import LanguageDataNotAvailable from './LanguageDataNotAvailable';
import MainHeader from './MainHeader';
import QuestionList from './QuestionList';
import Text from './Text';

function JobApply() {
  const {
    jobStore,
    uiStore: { language, selectJob, t, preview },
    companyStore,
  } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobID } = useParams();

  selectJob(jobID);
  let [form, setForm] = useState(null);
  let job = jobStore.items.get(jobID);
  let data = job && job.languages.find((l) => l.language === language);
  let company = companyStore.current.name;
  let backUrl = `/${company}/${language}/${jobID}${location.search}`;
  let referralUuid = useQueryParams('referral_uuid');
  let referredSite = document.referrer.substring(0, 200);
  const frame = useQueryParams('frame');
  usePreviewCheck(jobID);

  useDocumentTitle(data && data.title);

  const formState = job && job.formState;

  useFormLoad(jobID, jobStore, () => {
    navigate(backUrl);
  });

  useEffect(() => {
    if (job && job.formState === 'loaded' && data) {
      setForm(
        JobApplication.create({
          questions: getSnapshot(data.form),
          language,
          job: getSnapshot(job),
          referralUuid,
          referredSite,
        })
      );
    }
  }, [job, formState, data, language, referralUuid, referredSite]);

  if (job && job.formState === 'loaded' && !data) {
    return (
      <Wrapper>
        <MainHeader>
          <BackButton to={backUrl} text={t('backToDetail')} />
        </MainHeader>
        <LanguageDataNotAvailable />
      </Wrapper>
    );
  }

  if (!job || !form) {
    return null;
  }

  async function handleSubmit(form) {
    const success = await form.apply(frame ? false : true);
    if (frame && success) {
      const data = {
        name: form.name,
        email: form.email,
        type: 'apply_success',
      };

      if ('parentIFrame' in window) {
        window.parentIFrame.sendMessage(data);
      }

      if (window.parent !== window) {
        window.parent.postMessage(data, '*');
      }
    }
  }

  return (
    <Wrapper>
      {!frame && (
        <>
          <MainHeader>
            <BackButton to={backUrl} text={t('backToDetail')} disabled={form.disabled} />
          </MainHeader>
          <TitleContainer>
            <JobHeader job={job} showDeadline langData={data} />
            <MobileRemove>
              <Spacer top={30} bottom={30}>
                <Separator />
              </Spacer>
            </MobileRemove>
          </TitleContainer>
        </>
      )}
      <ApplyForm>
        <InputField form={form} required field="name" />
        <InputField form={form} required field="email" />
        {job.includePhone && <InputField form={form} required={job.requirePhone} field="phone" />}
        {job.includeSsn && <InputField form={form} required={job.requireSsn} field="ssn" />}
        {job.includeSex && <InputField form={form} required={job.requireSex} field="sex" />}
        {job.includeCv && <FileField form={form} required={job.requireCv} field="cv" />}
        {job.includeCoverLetter && (
          <FileField form={form} required={job.requireCoverLetter} field="coverLetter" />
        )}
        <QuestionList form={form} />
        <AcceptTerms form={form} />
        <SubmitWrapper>
          <RoundButton
            onClick={
              preview || form.hasErrors || form.applying || !form.termsAccepted
                ? null
                : () => handleSubmit(form)
            }
            disabled={preview || form.hasErrors || form.applying || !form.termsAccepted}
            title={!form.termsAccepted ? t('terms_agreement_required') : ''}
          >
            {!form.applying ? t('apply_submit') : t('apply_submitting')}
          </RoundButton>
          {form.hasErrors && <Text type="error">{t('apply_form_has_errors')}</Text>}
          {form.nonFieldErrors && <Text type="error">{form.nonFieldErrors}</Text>}
        </SubmitWrapper>
      </ApplyForm>
    </Wrapper>
  );
}
export default observer(JobApply);

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 40px 0;
  max-width: 720px;
  @media (max-width: 720px) {
    padding: 40px;
  }

  @media (max-width: 460px) {
    padding: 40px 20px;
  }
`;

const TitleContainer = styled.div`
  width: 100%;
  max-width: 380px;
  margin: auto;
`;

const ApplyForm = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 30px auto;
  @media (max-width: 720px) {
    max-width: 380px;
  }
`;

const SubmitWrapper = styled.div`
  width: 100%;
  max-width: 550px;
  margin: 30px 0;
  display: flex;
  align-items: flex-end;
  flex-direction: column;

  @media (max-width: 480px) {
    button {
      width: 100%;
    }
  }
`;
